import React, { useEffect, useState } from 'react'
import { user } from 'ampersand-app'
import { useDispatch, useSelector } from 'react-redux'
import { makeStyles } from '@mui/styles'
import CheckIcon from '@assets/images/icons/check.svg'
import { ButtonIconSquare } from '@/modules/base/components/buttons'
import { ModalBig } from '@/modules/base/components/modal'
import ExpenseService from '@/modules/expense/ExpenseService'
import EconomicAssetDrawer from '@/modules/economicAsset/components/drawer/economicAsset-drawer'
import EconomicAssetDetailView from '@/modules/economicAsset/components/drawer/economicAsset-detail-view'
import { openEditDrawer, closeEditDrawer, setEconomicAssetIdNull } from '@/redux/ducks/economicAsset/list'
import { BookingService } from '@/modules/banking/services'
import { ACCOUNT_CATEGORY } from '@/modules/economicAsset/constants'
import { convertDateToApiDateFormat } from '@/modules/_helpers/formatDate'
import lang from '@/lang'
import { formatCurrency } from '@/modules/_helpers/formatCurrency'
import clsx from 'clsx'

const useStyles = makeStyles((theme) => ({
  modalContent: {
    display: 'flex',
    flexDirection: 'column',
  },
  subtitle: {
    paddingTop: 10,
    fontSize: theme.fontSizes.default,
  },
  bold: {
    fontWeight: theme.fontWeights.medium,
    paddingTop: 20,
  },
  small: {
    fontSize: theme.fontSizes.smaller,
  },
  subtitleLink: {
    paddingTop: 30,
    fontSize: theme.fontSizes.smaller,
  },
}))

const CreateEconomicAssetModal = (props) => {
  const classes = useStyles()
  const { expenseId, onClose, open } = props
  const state = useSelector(state => state.economicAsset.list)
  const [financeApiId, setFinanceApiId] = useState()
  const [expenseDate, setExpenseDate] = useState()
  const [investmentAccounts, setInvestmentAccounts] = useState([]) // Default to empty array
  const [accountCategoryEconomicAssetData, setAccountCategoryEconomicAssetData] = useState()
  const [supplier, setSupplier] = useState()
  const [categoryDescription, setCategoryDescription] = useState()
  const [categoryNo, setCategoryNo] = useState()
  const [totalNet, setTotalNet] = useState()
  const [totalGross, setTotalGross] = useState()
  const [modalOpen, setModalOpen] = useState(open)
  const dispatch = useDispatch()
  const isSmallBusiness = user.isSmallBusiness
  const {
    editDrawerOpen,
  } = state

  useEffect(() => {
    const fetchInvestmentAccounts = async() => {
      const { data: { chartsOfAccount: { accounts } } } = await BookingService.chartOfAccounts.getList({ accountCategory: ACCOUNT_CATEGORY.investmentAccounts })
      setInvestmentAccounts(accounts)
    }
    fetchInvestmentAccounts()
  }, [])

  useEffect(() => {
    if (expenseId && investmentAccounts.length > 0) {
      ExpenseService.getById(expenseId)
        .then(({ data: responseData }) => {
          setFinanceApiId(responseData.financeApiId)
          setExpenseDate(convertDateToApiDateFormat(responseData.date))
          setSupplier(responseData.payee)

          const investmentPosition = responseData.positions.find(
            item => investmentAccounts.some(account => account.accountNo === item.bookkeepingAccountNo),
          )

          if (investmentPosition) {
            setAccountCategoryEconomicAssetData(investmentPosition)
            setCategoryDescription(investmentPosition.bookkeepingAccountDescriptionCustom)
            setCategoryNo(investmentPosition.bookkeepingAccountNo)
            setTotalNet(investmentPosition.totalNet)
            setTotalGross(investmentPosition.totalGross)
          }
        })
    }
  }, [expenseId, investmentAccounts])

  const handleAbort = () => {
    onClose()
  }

  const onCloseEditDrawer = () => {
    dispatch(closeEditDrawer())
    dispatch(setEconomicAssetIdNull())
    onClose()
  }
  const onOpenEditDrawer = () => {
    dispatch(openEditDrawer())
    setModalOpen(false)
  }

  const actions = [
    <ButtonIconSquare variant='outline' label={lang.economicAsset.modal.abortButton} onClick={handleAbort} />,
    <ButtonIconSquare
      type='submit'
      icon={CheckIcon}
      label={lang.economicAsset.modal.submitButton}
      onClick={onOpenEditDrawer} />,
  ]

  const modalSubtitle = (
    <div className={classes.modalContent}>
      <span className={classes.subtitle}>{lang.economicAsset.modal.subtitleHeader}</span>
      <span className={clsx(classes.subtitle, classes.bold)}>{lang.economicAsset.modal.subtitleInfoHeader}</span>
      <span className={classes.subtitle}>{lang.economicAsset.modal.subtitleSupplier} {supplier}</span>
      <span className={classes.subtitle}>{lang.economicAsset.modal.subtitleCategory} {categoryDescription} ({categoryNo})</span>
      <span className={classes.subtitle}>{lang.economicAsset.modal.subtitleTotalNet} {isSmallBusiness ? formatCurrency(totalGross) : formatCurrency(totalNet)}</span>
      <span className={clsx(classes.subtitle, classes.bold)}>{lang.economicAsset.modal.subtitleCreate}</span>
      <span className={clsx(classes.subtitle, classes.small)}>{lang.economicAsset.modal.subtitleCreateInfo}</span>
      <div className={classes.subtitleLink} dangerouslySetInnerHTML={{ __html: lang.economicAsset.modal.subtitleLink }}></div>
    </div>
  )

  return (
    <>
      <ModalBig
        title={lang.economicAsset.modal.title}
        subtitle={modalSubtitle}
        open={modalOpen}
        actions={actions}
        onClose={handleAbort}>
      </ModalBig>
      <EconomicAssetDrawer open={editDrawerOpen} onClose={onCloseEditDrawer} enableBackdropClick={true}>
        <EconomicAssetDetailView
          accountCategoryEconomicAssetData={accountCategoryEconomicAssetData}
          expenseFinanceApiId={financeApiId}
          expenseDate={expenseDate}
          investmentAccounts={investmentAccounts}
          onClose={onCloseEditDrawer}
          onAbort={onCloseEditDrawer}
        />
      </EconomicAssetDrawer>
    </>
  )
}

export default CreateEconomicAssetModal
